import { useEffect, useState } from "react";
import DocumentTitle from "react-document-title";
import CompetitionsService, { CompetitionType, CompetitionTypes, CompetitionsUsersType } from "../../services/CompetitionsService";
import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import styles from './index.module.sass';
import { currencyNeutreFormatter, percentFormatter } from "../../utils/numberFormatter";
import { useHistory, useParams } from "react-router-dom";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DataAndCartLines } from "../../services/PortfolioModelWalletService";
import BackButton from "../../components/buttons/BackButton";
import Auth from "../../Auth/Auth";
import { ErrorModal } from "../../components/Modals/ErrorModal";
import { formatCompetitionDate, parseCompetitionDate } from "@/utils/DateFormatter";
import { canJoinCompetitionWithPlans, ManagedPlanOnlyMessage } from "@/utils/CompetitionPlans";
import { sanitizeCompetitionHtml } from "@/utils/sanitizeCompetitionHtml";

// Estado de inscripción cancelada (CompetitionsUsers.StatusUser.Canceled en el backend)
const CanceledInscriptionStatus = 2;

const options: Highcharts.Options =
{
    title:
    {
        text: '',
    },
    xAxis: { visible: false },
    yAxis: { visible: false },
    legend: { enabled: false },
    exporting: { enabled: false },
    credits: { enabled: false },
    chart:
    {
        type: 'bar',
        margin: 0,
        height: 24,
        backgroundColor: "transparent",
        borderColor: "transparent"
    },
    tooltip:
    {
        headerFormat: "",
        valueSuffix: '%',
        outside: true,
    },
    plotOptions:
    {
        bar:
        {
            stacking: 'percent',
            groupPadding: 0,
            pointPadding: 0,
            borderRadius: 4,
            borderColor: "transparent",
        },
    },
};

const Competition = () =>
{
    let history = useHistory();
    const { competitionId } = useParams<any>();
    const [competition, setCompetition] = useState<CompetitionType>();
    const [competitionUsers, setCompetitionUsers] = useState<CompetitionsUsersType[]>();
    const [series, setSeries] = useState<any[]>([]);
    const [error, setError] = useState({ status: false, message: '' });
    const [loading, setLoading] = useState<boolean>(true);
	const activePlan = Auth.getActivePlan();
    const user: any = Auth.getUserProfile();

    // Mismas condiciones que la tarjeta del listado y el detalle promocionado: el usuario no está inscrito, la
    // competición no ha terminado y, si no admite inscripciones fuera de plazo, todavía no ha empezado.
    // Este detalle incluye las inscripciones canceladas; no cuentan como inscrito, igual que en el backend
    const competitionStartDate = parseCompetitionDate(competition?.startDate);
    const competitionEndDate = parseCompetitionDate(competition?.endDate);
    const isUserInscribed = competition?.competitionsUsers?.some(competitionUser => competitionUser.userId === user?.id && competitionUser.statusUserInCompetition !== CanceledInscriptionStatus) ?? false;
    const isCompetitionIniciated = competitionStartDate !== null && competitionStartDate <= new Date();
    const canParticipate = !isUserInscribed
        && competitionEndDate !== null && competitionEndDate >= new Date()
        && (competition?.allowOutdatedIncriptions === true || (competition?.allowOutdatedIncriptions === false && !isCompetitionIniciated));

    useEffect(() =>
    {
        CompetitionsService.getCompetition(competitionId).then((response: CompetitionType) =>
        {
            setCompetition(response);
        },
        (error: any) =>
        {
            // Sin competición la página muestra su mensaje de error de carga; no se guarda el error de axios
            // en el estado del modal (su "status" es el código HTTP y abría el aviso de plan no compatible)
            console.error("Error al cargar la competición", error);
        })
        .finally(() =>
        {
            setLoading(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
        if(competition !== undefined)
        {
            var competitionsUsers = competition.competitionsUsers.map((competitionUser: CompetitionsUsersType) =>
            {
                let performance = 0;
                const valueVirtualPortfolios = competitionUser.portfolioModel.valueVirtualPortfolios;

                if(valueVirtualPortfolios.length !== 0 && valueVirtualPortfolios[0].ammount !== 0)
                {
                    const amountDistintInitial = valueVirtualPortfolios.filter(vVP => vVP.ammount !== valueVirtualPortfolios[0].ammount);
                    if(amountDistintInitial.length > 0)
                    {
                        performance = (amountDistintInitial[amountDistintInitial.length - 1].ammount - valueVirtualPortfolios[0].ammount) / valueVirtualPortfolios[0].ammount;
                    }
                }
                competitionUser.performance = performance;
                return competitionUser;
            });
            const sortedCompetitionsUsers = competitionsUsers.sort((a: CompetitionsUsersType, b: CompetitionsUsersType) => ((a.performance ?? 0) > (b.performance ?? 0) ? -1 : 1));
            setCompetitionUsers(sortedCompetitionsUsers);
        }
    }, [competition]);

    useEffect(() =>
    {
        if(competitionUsers !== undefined)
        {
            var seriesAux: any[] = [];
            competitionUsers.forEach((competitionUser: CompetitionsUsersType) =>
            {
                var serie: any[] = [];
                competitionUser.portfolioModel.jsonFundsData.forEach((dataAndCartLine: DataAndCartLines) =>
                {
                    serie.push(
                    {
                        name: dataAndCartLine.fund.name,
                        data: [dataAndCartLine.modelPercentage]
                    });
                });
                seriesAux.push(serie.sort((serieItemA, serieItemB) => serieItemA.data[0] - serieItemB.data[0]));
            });
            setSeries(seriesAux);
        }
    }, [competitionUsers]);

    return(
        <DocumentTitle title={`IronIA - Competiciones - ${competition?.name}`}>
            <section className={`${styles.competition}`}>
                <BackButton />
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                        <h2>Competiciones</h2>
                    </div>
                </div>
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12">
                        <p className="descriptioncompetition ">
                            ¿Quieres aprender jugando?
                        </p>
                        <p className="descriptioncompetition ">
                            Las competiciones de IronIA te permiten conocer el mercado de los
                            fondos de inversión gracias a nuestro simulador. Crea carteras
                            modelo, sin ningún tipo de inversión real y compite en
                            rentabilidad con otras carteras.
                        </p>

                        <p className="descriptioncompetition ">
                            La cartera más rentable gana... y lo que gana depende del reto.
                        </p>
                    </div>
                </div>
                {loading && (<Spinner size={SpinnerSize.large} />)}
                {!loading && competition === undefined && <p>Se ha producido un error al traer la información.</p>}
                {!loading && competition !== undefined &&
                (
                    <>
                        <section className="ms-Grid-row">
                            <div style={{ display: "flex" }}>
                                <h3>{competition?.name}</h3>
                                {canParticipate && <PrimaryButton
                                    className="rounded"
                                    style={{ display: "flex", justifyContent: "right" }}
                                    onClick={async () =>
                                    {
										// Con un plan gestionado como activo solo se bloquea si no hay otro plan compatible
										if(!(await canJoinCompetitionWithPlans(activePlan, user?.id)))
                                        {
											setError({status: true, message: ManagedPlanOnlyMessage})
										}
                                        else
                                        {
											if(competition !== undefined)
                                            {
                                                history.push(`/competitions/join/${competition.id}`)
                                            }
                                        }
                                    }}
                                >
                                    Participar
                                </PrimaryButton>}
                            </div>
                            <span>
                                Del {formatCompetitionDate(competition?.startDate)}
                                {" "}al
                                {" "} {formatCompetitionDate(competition?.endDate)}
                            </span>
                            <div className="ms-Grid-col ms-sm12">
                                {/* Sumario saneado con lista blanca de etiquetas (auditoría G4-07) */}
                                <p dangerouslySetInnerHTML={{ __html: sanitizeCompetitionHtml(competition?.summary) }} />
                            </div>
                            <p className={styles.type}>Tipo: {CompetitionTypes[competition?.type || 0]}</p>
                            <p className={styles.price}>
                                Precio: {" "}
                                {competition?.price === 0 && <>Inscripción gratuita</>}
                                {competition?.price !== 0 && competition?.typeOfPrice === 0 &&
                                    <>{currencyNeutreFormatter.format(competition.price)}{" "} Elements IA </>
                                }
                                {competition?.typeOfPrice === 1 &&
                                    <>{currencyNeutreFormatter.format(competition.price)}{" "} Elements Agua</>
                                }
                                {(competition?.typeOfPrice === 2  || competition?.typeOfPrice === 3) &&
                                    <>{currencyNeutreFormatter.format(competition.price)}{" "} €</>
                                }
                            </p>
                        </section>
                        <section className="ms-Grid-row">
                            <div className={`${styles.tableContainer} background-tertiary`}>
                                {competitionUsers &&
                                (
                                    <div className={styles.tableScroll}>
                                        <table>
                                            <thead className="background-shadow">
                                                <tr>
                                                    <td className={styles.positionHead}>#</td>
                                                    <td className={styles.performanceHead}>Rentabilidad</td>
                                                    <td className={styles.nameHead}>Cartera</td>
                                                    <td className={styles.compositionHead}>Composisión</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {competitionUsers.map((competitionUser, competitionUserIndex) => (
                                                    <tr key={`competitionUserIndex{competitionUserIndex}`}>
                                                        <td className={styles.position}>{competitionUserIndex + 1}</td>
                                                        <td className={styles.performance}>{percentFormatter.format(competitionUser.performance ?? 0)}</td>
                                                        <td className={styles.name}>{competitionUser.portfolioModel.name}</td>
                                                        <td className={styles.composition}>
                                                            <HighchartsReact highcharts={Highcharts}
                                                                options={{
                                                                    ...options,
                                                                    series: series[competitionUserIndex]
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </section>
						<ErrorModal
                            title="Tu Plan actual no es compatible con la competición"
                            body={<div><p>{error.message}</p></div>}
                            btnSubmitText="Aceptar" showModal={error.status} 
                            onSubmit={() => setError({status: false, message: ""})} onClose={() => setError({status: false, message: ""})}
                        />
                    </>
                )}
            </section>
        </DocumentTitle>
    );
};

export default Competition;